import { Component, ElementRef, Renderer2, Input, Output, EventEmitter } from '@angular/core';
import { User } from '../models/user';
import { AuthService } from '../services/authService';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/internal/operators/catchError';
import { Observable, of } from 'rxjs';
import { FoodModel } from '../models/foodModel';
import { validate } from 'class-validator';
import { UserMealEntryModel } from '../models/userMealEntryModel';
import { Router } from '@angular/router';
import Awesomplete from 'awesomplete';
@Component({
  selector: 'app-add-food-to-plan',
  templateUrl: './add-food-to-plan.component.html',
  styleUrl: './add-food-to-plan.component.scss',
  standalone: false
})
export class AddFoodToPlanComponent {
  userMealEntryModel?: UserMealEntryModel;
  selectedFoodName?: string;
  selectedFood?: FoodModel;
  quantityAdjustedSelectedFood?: FoodModel;
  selectedFoodServingSize = 0;
  user = new User('', false);
  foodNames: string[] = [];
  foods: FoodModel[] = [];
  selectedFoodDetails: FoodModel = new FoodModel(0, '', 0, 0, 0, 0, 0, 0, 0, 0, 0);
  @Input() mealType: string = '';
  mealTypeId: number | undefined;
  foodNameErrorMsg: string | undefined;
  mealTypeIdErrorMsg: string | undefined;
  quantityErrorMsg: string | undefined;
  response: any;
  renderer: any;
  isFoodAddedSuccessfully = false;
  dismissible = true;
  awesomplete: Awesomplete | undefined;
  @Output() foodAdded = new EventEmitter<number>();
  @Output() foodCreated = new EventEmitter<void>();

  constructor(private http: HttpClient, private authService: AuthService, private el: ElementRef, renderer: Renderer2, private router: Router) {
    this.renderer = renderer;
  }

  async ngOnInit() {
    const isLoggedIn = await this.checkIfUserIsLoggedIn();
    if (isLoggedIn) {
      await this.getListOfFoods();

      // Wait for DOM to be ready
      setTimeout(() => {
        const inputs = document.querySelectorAll(`input[id^="foodName_"]`);
        inputs.forEach((input) => {
          this.awesomplete = new Awesomplete(input, {
            list: this.foodNames,
            minChars: 1,
            autoFirst: true,
            maxItems: 5,
            filter: function (text: string, input: string) {
              return text.toLowerCase().indexOf(input.toLowerCase()) !== -1;
            }
          });

          // Force dropdown to stay on top
          const dropdown = input.parentElement?.querySelector('.awesomplete ul');
          if (dropdown) {
            (dropdown as HTMLElement).style.zIndex = '10000';
          }
        });
      }, 100);
    }
  }

  public checkIfUserIsLoggedIn(): Promise<boolean> {
    return new Promise((resolve) => {
      this.authService.isLoggedIn().subscribe((response: any) => {
        this.user.loggedin = response.isAuthenticated;
        this.user.username = response.userName ?? '';
        resolve(this.user.loggedin);
      });
    });
  }

  public handleFoodCreated(): void {
    this.getListOfFoods();
  }

  public getListOfFoods(): Promise<void> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
      this.http
        .get<any>('/api/Foods/get_foods_list', {
          headers: headers,
        })
        .pipe(catchError(this.handleError<any>('getListOfFoods')))
        .subscribe({
          next: (result) => {
            if (result.success) {
              this.bindFoodsListToFormControl(result.foodsList);
              this.mapFoodsToFoodModel(result.foodsList);
              resolve();
            } else {
              console.error('Error getting foods list');
              reject('Error getting foods list');
            }
          },
          error: (error) => {
            console.error(error);
            reject(error);
          },
        });
    });
  }

  public bindFoodsListToFormControl(foodsList: []) {
    // iterate over the foods array and assign each food.name to the foodNames array
    foodsList.forEach((food: any) => {
      this.foodNames.push(food.name);
    });
    this.formatFoodNames();
  }

  public formatFoodNames() {
    this.foodNames = this.foodNames.map(name => name.trim());
  }

  public mapFoodsToFoodModel(foods: FoodModel[]) {
    for (const food of foods) {
      this.foods.push(
        new FoodModel(
          food.id,
          food.name,
          food.servingSize,
          food.unitsOfMeasureId,
          food.carbs,
          food.alcohol,
          food.fiber,
          food.netcarbs,
          food.fat,
          food.protein,
          food.calories
        )
      );
    }
  }

  public handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }

  public getSelectedFoodDetails(e: Event): void {
    // Only update if we have a selected food name
    if (e.target) {
      // Find the matching food from our foods array
      this.selectedFood = this.foods.find(
        (food) => food.name.toLowerCase() === (e.target as HTMLInputElement).value.toLowerCase()
      );

      // Only create new FoodModel if we found a matching food
      if (this.selectedFood) {
        this.quantityAdjustedSelectedFood = new FoodModel(
          this.selectedFood.id,
          this.selectedFood.name,
          this.selectedFood.servingSize,
          this.selectedFood.unitsOfMeasureId,
          this.selectedFood.carbs,
          this.selectedFood.alcohol,
          this.selectedFood.fiber,
          this.selectedFood.netcarbs,
          this.selectedFood.fat,
          this.selectedFood.protein,
          this.selectedFood.calories
        );

        this.selectedFoodServingSize = this.selectedFood.servingSize;
        this.updateCaloriesAndMacrosForFoodEntry();
      }
    }
  }

  public updateCaloriesAndMacrosForFoodEntry(event?: Event): void {
    const inputQuantity = event
      ? (event.target as HTMLInputElement).valueAsNumber
      : this.selectedFood?.servingSize!;

    this.quantityAdjustedSelectedFood = new FoodModel(
      this.selectedFood?.id!,
      this.selectedFood?.name!,
      this.selectedFood?.servingSize!,
      this.selectedFood?.unitsOfMeasureId!,
      this.selectedFood?.carbs!,
      this.selectedFood?.alcohol!,
      this.selectedFood?.fiber!,
      this.selectedFood?.netcarbs!,
      this.selectedFood?.fat!,
      this.selectedFood?.protein!,
      this.selectedFood?.calories!
    );

    const macros = ['netCarbs', 'alcohol', 'fat', 'protein', 'calories'];
    const caloriesPerGramOfFat = 9;
    const caloriesPerGramOfAlcohol = 7;
    const caloriesPerGramOfProteinOrCarb = 4;
    let netCarbs: string, protein: string, fat: string, alcohol: string;
    macros.forEach((macro) => {
      if (this.selectedFood && this.quantityAdjustedSelectedFood) {
        let fixed = '';
        switch (macro) {
          case 'netCarbs':
            netCarbs = fixed = (
              (inputQuantity / this.selectedFood?.servingSize!) *
              this.selectedFood?.netcarbs!
            ).toFixed(2);
            this.quantityAdjustedSelectedFood.netcarbs = parseFloat(fixed);
            break;
          case 'alcohol':
            alcohol = fixed = (
              (inputQuantity / this.selectedFood?.servingSize!) *
              this.selectedFood?.alcohol!
            ).toFixed(2);
            this.quantityAdjustedSelectedFood.alcohol = parseFloat(fixed);
            break;
          case 'fat':
            fat = fixed = (
              (inputQuantity / this.selectedFood?.servingSize!) *
              this.selectedFood?.fat!
            ).toFixed(2);
            this.quantityAdjustedSelectedFood.fat = parseFloat(fixed);
            break;
          case 'protein':
            protein = fixed = (
              (inputQuantity / this.selectedFood?.servingSize!) *
              this.selectedFood?.protein!
            ).toFixed(2);
            this.quantityAdjustedSelectedFood.protein = parseFloat(fixed);
            break;
          case 'calories':
            const netCarbCalories =
              Number.parseFloat(netCarbs) * caloriesPerGramOfProteinOrCarb;
            const alcoholCalories =
              Number.parseFloat(alcohol) * caloriesPerGramOfAlcohol;
            const proteinCalories =
              Number.parseFloat(protein) * caloriesPerGramOfProteinOrCarb;
            const fatCalories = Number.parseFloat(fat) * caloriesPerGramOfFat;
            const totalCalories =
              netCarbCalories + alcoholCalories + fatCalories + proteinCalories;
            fixed = totalCalories.toFixed(2);
            this.quantityAdjustedSelectedFood.calories = parseFloat(fixed);
            break;
        }
      }
    });
    this.setMealTypeId();
    this.userMealEntryModel = new UserMealEntryModel(
      this.mealTypeId!,
      this.selectedFood?.id!,
      inputQuantity
    );
  }

  public setMealTypeId() {
    switch (this.mealType) {
      case "Breakfast":
        this.mealTypeId = 1;
        break;
      case "Lunch":
        this.mealTypeId = 2;
        break;
      case "Dinner":
        this.mealTypeId = 3;
        break;
      case "Snacks":
        this.mealTypeId = 4;
        break;
    }
  }

  public async validateUserMealEntry(): Promise<boolean> {
    let isValid = false;
    if (this.userMealEntryModel) {
      const errors = await validate(this.userMealEntryModel);
      if (errors.length > 0) {
        console.error('Validation failed. Errors: ', errors);
        errors.forEach((error) => {
          if (error.property === 'mealTypeId') {
            const defaultMsg = "Invalid input for meal type id";
            this.mealTypeIdErrorMsg = error.constraints?.["min"] ?? defaultMsg;
            this.mealTypeIdErrorMsg = error.constraints?.["max"] ?? defaultMsg;
          }
          if (error.property === 'quantity') {
            const defaultMsg = "Invalid input for quantity";
            this.quantityErrorMsg = error.constraints?.['isPositive'] ?? defaultMsg;
            this.quantityErrorMsg = error.constraints?.['max'] ?? defaultMsg;
          }
        });
      } else {
        this.foodNameErrorMsg = '';
        isValid = true;
      }
    }
    const formElement = this.el.nativeElement.querySelector(
      'form[name="addFoodToPlanForm"]'
    );
    if (formElement) {
      this.renderer.addClass(formElement, 'was-validated');
    }
    return isValid;
  }

  public async addFoodToPlan(): Promise<void> {
    const formElement = this.el.nativeElement.querySelector(
      'form[name="addFoodToPlanForm"]'
    );
    if (formElement) {
      this.renderer.removeClass(formElement, 'needs-validation');
    }

    const userMealEntryIsValid = await this.validateUserMealEntry();

    if (!userMealEntryIsValid) {
      this.isFoodAddedSuccessfully = false;
      return;
    }
    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    this.http
      .post('/api/UserMeals/post_user_meal', this.userMealEntryModel, {
        headers: headers,
      })
      .pipe(catchError(this.handleError<any>('addFoodToPlan')))
      .subscribe({
        next: (response) => {
          this.response = response;
          if (!response) {
            console.error('response is undefined');
          } else if (response?.success == false) {
            for (
              let errorMessage = 0;
              errorMessage < response.errorMessages.length;
              errorMessage++
            ) {
              console.error(response.errorMessages[errorMessage]);
              const errorMsgLowerCase = response.errorMessages[errorMessage].toLowerCase().trim();
              if (errorMsgLowerCase.indexOf('exception') != -1) {
                throw new Error(response.errorMessages[errorMessage]);
              }

              if (errorMsgLowerCase.indexOf('user id not found.') != -1 || errorMsgLowerCase.indexOf('user is not authenticated.') != -1) {
                this.router.navigate(['/login']);
              }
            }
          } else {
            // success
            this.isFoodAddedSuccessfully = true;
            this.quantityAdjustedSelectedFood = undefined;
            this.selectedFood = undefined;
            this.selectedFoodName = ''; // Clear the food name
            this.selectedFoodServingSize = 0; // Reset serving size

            const formElement = this.el.nativeElement.querySelector('form[name="addFoodToPlanForm"]');
            if (formElement) {
              formElement.reset(); // Reset the form
              this.renderer.removeClass(formElement, 'was-validated');
              this.renderer.addClass(formElement, 'needs-validation');
            }
            this.foodAdded.emit(this.mealTypeId);
          }
        },
        error: (error) => {
          this.response = error;
          console.error('Error received:', error.message);
        },
      });
  }
}
